import { errorMessage } from "@atlas/shared";
import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, ShieldAlert, UserPlus, X } from "lucide-react";
import {
  fetchAdminReferrals,
  fetchAdminReferralEvents,
  resolveAdminReferralEvent,
  type AdminReferralEvent,
  type ReferralFlag,
  type ReferralStatus,
} from "../../api/referrals";
import { AtlasPanel } from "../../components/AtlasPanel";
import { Badge } from "../../components/Badge";
import { Button } from "../../components/Button";
import { EmptyState } from "../../components/EmptyState";
import { Skel } from "../../components/Skeleton";
import { StatBlock } from "../../components/StatBlock";
import { useAuth } from "../../hooks/useAuth";
import { AdminShell, AccessDenied } from "./AdminTabs";
import { formatDateTime } from "./usage-ui";

// Admin invites (spec 21 / ATL-96): totals, 30-day series, top referrers and
// the fraud list with credit / reject actions.

const SUMMARY_KEY = ["admin/referrals"];
const EVENTS_KEY = ["admin/referrals/events"];

const STATUS_OPTIONS: { value: ReferralStatus | "all"; label: string }[] = [
  { value: "review", label: "In review" },
  { value: "pending", label: "Pending" },
  { value: "credited", label: "Credited" },
  { value: "rejected", label: "Rejected" },
  { value: "expired", label: "Expired" },
  { value: "all", label: "All statuses" },
];

const FLAG_OPTIONS: { value: ReferralFlag | "all"; label: string }[] = [
  { value: "all", label: "Any flag" },
  { value: "same_ip_as_referrer", label: "Same IP as referrer" },
  { value: "ip_burst", label: "IP burst" },
];

const FLAG_LABEL: Record<string, string> = {
  same_ip_as_referrer: "Same IP",
  ip_burst: "IP burst",
};

const STATUS_VARIANT: Record<ReferralStatus, "gold" | "info" | "neutral" | "danger" | "success"> = {
  pending: "info",
  review: "danger",
  credited: "gold",
  rejected: "neutral",
  expired: "neutral",
};

const selectStyle: React.CSSProperties = {
  height: 30,
  padding: "0 10px",
  background: "var(--surface-sunken)",
  border: "1px solid var(--border-default)",
  borderRadius: "var(--radius-md)",
  color: "var(--text-primary)",
  fontSize: 12,
  fontFamily: "var(--font-sans)",
  outline: "none",
};

const inputStyle: React.CSSProperties = {
  height: 30,
  padding: "0 10px",
  background: "var(--surface-sunken)",
  border: "1px solid var(--border-default)",
  borderRadius: "var(--radius-md)",
  color: "var(--text-primary)",
  fontSize: 12,
  fontFamily: "var(--font-sans)",
  outline: "none",
  boxSizing: "border-box",
  minWidth: 160,
};

const thStyle: React.CSSProperties = { fontSize: 9, textAlign: "left", padding: "0 10px 8px 0", whiteSpace: "nowrap" };
const tdStyle: React.CSSProperties = { padding: "9px 10px 9px 0", verticalAlign: "top", borderTop: "1px solid var(--border-subtle)", fontSize: 12 };

function formatDay(value: string | null): string {
  if (!value) return "—";
  return new Date(value).toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

// ─── Series (two lines: sign-ups and credited) ────────────────────────────────

function ReferralLines({ points }: { points: { date: string; signups: number; credited: number }[] }) {
  const [hover, setHover] = React.useState<number | null>(null);
  const width = 640;
  const height = 160;
  const pad = { top: 12, right: 12, bottom: 24, left: 30 };
  const innerW = width - pad.left - pad.right;
  const innerH = height - pad.top - pad.bottom;
  const max = Math.max(1, ...points.flatMap((p) => [p.signups, p.credited]));
  const x = (i: number) => pad.left + (points.length > 1 ? (i / (points.length - 1)) * innerW : innerW / 2);
  const y = (v: number) => pad.top + innerH - (v / max) * innerH;
  const fmt = (d?: string) => (d ? new Date(d).toLocaleDateString(undefined, { day: "numeric", month: "short" }) : "");
  const axisStyle: React.CSSProperties = { fontFamily: "var(--font-mono)", fontSize: 9, textTransform: "uppercase", letterSpacing: "0.08em", fill: "var(--text-muted)" };
  const hovered = hover != null ? points[hover] : null;
  const series: { key: "signups" | "credited"; label: string; color: string }[] = [
    { key: "signups", label: "Sign-ups", color: "var(--sky-500)" },
    { key: "credited", label: "Credited", color: "var(--accent)" },
  ];

  if (points.length === 0) {
    return <p style={{ fontSize: 13, color: "var(--text-muted)", margin: 0 }}>Nothing in the last 30 days.</p>;
  }

  return (
    <div style={{ background: "var(--surface-sunken)", borderRadius: "var(--radius-md)", padding: "12px 14px 8px", position: "relative" }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 4, gap: 12, flexWrap: "wrap" }}>
        <span className="eyebrow" style={{ fontSize: 10 }}>Last 30 days{hovered ? ` · ${fmt(hovered.date)}` : ""}</span>
        <div style={{ display: "flex", gap: 12 }}>
          {series.map((s) => (
            <span key={s.key} style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, color: "var(--text-secondary)" }}>
              <span aria-hidden="true" style={{ width: 12, height: 2, background: s.color, borderRadius: 1 }} />
              {s.label}
              {hovered && <span className="tnum" style={{ color: "var(--text-muted)" }}>{hovered[s.key]}</span>}
            </span>
          ))}
        </div>
      </div>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        style={{ width: "100%", height: "auto", display: "block" }}
        role="img"
        aria-label={`Referral sign-ups and credits over ${points.length} days`}
        onMouseLeave={() => setHover(null)}
        onMouseMove={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          const px = ((e.clientX - rect.left) / rect.width) * width;
          const idx = Math.round(((px - pad.left) / innerW) * (points.length - 1));
          setHover(Math.max(0, Math.min(points.length - 1, idx)));
        }}
      >
        {[0, 0.5, 1].map((t) => (
          <g key={t}>
            <line x1={pad.left} x2={width - pad.right} y1={pad.top + innerH * (1 - t)} y2={pad.top + innerH * (1 - t)} stroke="var(--border-subtle)" strokeWidth={1} />
            <text x={pad.left - 6} y={pad.top + innerH * (1 - t) + 3} textAnchor="end" style={axisStyle}>{Math.round(max * t)}</text>
          </g>
        ))}
        {series.map((s) => (
          <polyline
            key={s.key}
            points={points.map((p, i) => `${x(i).toFixed(1)},${y(p[s.key]).toFixed(1)}`).join(" ")}
            fill="none"
            stroke={s.color}
            strokeWidth={2}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        ))}
        {hover != null && (
          <g>
            <line x1={x(hover)} x2={x(hover)} y1={pad.top} y2={pad.top + innerH} stroke="var(--border-strong)" strokeWidth={1} strokeDasharray="3 3" />
            {series.map((s) => (
              <circle key={s.key} cx={x(hover)} cy={y(points[hover]![s.key])} r={3.5} fill={s.color} />
            ))}
          </g>
        )}
        <text x={pad.left} y={height - 6} style={axisStyle}>{fmt(points[0]?.date)}</text>
        <text x={width - pad.right} y={height - 6} textAnchor="end" style={axisStyle}>{fmt(points[points.length - 1]?.date)}</text>
      </svg>
    </div>
  );
}

// ─── Fraud list row ───────────────────────────────────────────────────────────

function PersonCell({ person }: { person: { username: string | null; name: string | null; email: string } }) {
  return (
    <div style={{ minWidth: 0, maxWidth: 220 }}>
      <div style={{ color: "var(--text-primary)", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {person.name?.trim() || person.username || "Unnamed"}
        {person.username && (
          <span style={{ fontFamily: "var(--font-mono)", fontWeight: 400, color: "var(--text-muted)", marginLeft: 6 }}>@{person.username}</span>
        )}
      </div>
      <div style={{ color: "var(--text-muted)", fontSize: 11, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{person.email}</div>
    </div>
  );
}

function EventRow({ event }: { event: AdminReferralEvent }) {
  const qc = useQueryClient();
  const [rejecting, setRejecting] = React.useState(false);
  const [note, setNote] = React.useState("");

  const mutation = useMutation({
    mutationFn: (body: { action: "credit" | "reject"; note?: string }) => resolveAdminReferralEvent(event.id, body),
    onSuccess: () => {
      setRejecting(false);
      setNote("");
      void qc.invalidateQueries({ queryKey: SUMMARY_KEY });
      void qc.invalidateQueries({ queryKey: EVENTS_KEY });
    },
  });

  const open = event.status === "pending" || event.status === "review";

  return (
    <tr>
      <td style={{ ...tdStyle, whiteSpace: "nowrap", color: "var(--text-secondary)" }}>{formatDateTime(event.createdAt)}</td>
      <td style={tdStyle}><PersonCell person={event.referrer} /></td>
      <td style={tdStyle}>
        <PersonCell person={event.invitee} />
        <div style={{ display: "flex", gap: 8, marginTop: 3, fontSize: 11, color: "var(--text-muted)", flexWrap: "wrap" }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 3, color: event.invitee.emailVerified ? "var(--spruce-300)" : "var(--text-muted)" }}>
            {event.invitee.emailVerified ? <Check size={11} /> : <X size={11} />}
            {event.invitee.emailVerified ? "Verified" : "Unverified"}
          </span>
          <span>First activity {formatDay(event.invitee.firstActivityAt)}</span>
        </div>
      </td>
      <td style={{ ...tdStyle, color: "var(--text-secondary)" }}>{event.source}</td>
      <td style={tdStyle}>
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          {event.flags.length === 0 && <span style={{ color: "var(--text-faint)" }}>—</span>}
          {event.flags.map((f) => (
            <Badge key={f} variant="danger">{FLAG_LABEL[f] ?? f}</Badge>
          ))}
        </div>
      </td>
      <td className="tnum" style={{ ...tdStyle, textAlign: "right", color: event.ipCollisions > 0 ? "var(--text-primary)" : "var(--text-muted)" }}>
        {event.ipCollisions}
      </td>
      <td style={tdStyle}>
        <Badge variant={STATUS_VARIANT[event.status]}>{event.status}</Badge>
        {event.creditedAt && (
          <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 3 }}>{formatDay(event.creditedAt)}</div>
        )}
      </td>
      <td style={{ ...tdStyle, color: "var(--text-secondary)", maxWidth: 200 }}>{event.note ?? <span style={{ color: "var(--text-faint)" }}>—</span>}</td>
      <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>
        {open && !rejecting && (
          <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<Check size={12} />}
              disabled={mutation.isPending}
              onClick={() => mutation.mutate({ action: "credit" })}
            >
              Credit
            </Button>
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<X size={12} />}
              disabled={mutation.isPending}
              onClick={() => setRejecting(true)}
            >
              Reject
            </Button>
          </div>
        )}
        {open && rejecting && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              mutation.mutate({ action: "reject", note: note.trim() || undefined });
            }}
            style={{ display: "flex", gap: 6, alignItems: "center", justifyContent: "flex-end" }}
          >
            <input
              autoFocus
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Reason (kept on the row)"
              maxLength={200}
              style={inputStyle}
            />
            <Button type="submit" variant="danger" size="sm" disabled={mutation.isPending}>
              {mutation.isPending ? "Rejecting…" : "Reject"}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => { setRejecting(false); setNote(""); }}>
              Cancel
            </Button>
          </form>
        )}
        {mutation.isError && (
          <div style={{ fontSize: 11, color: "var(--status-danger)", marginTop: 4, textAlign: "right" }}>
            {errorMessage(mutation.error, "Failed")}
          </div>
        )}
      </td>
    </tr>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function AdminReferralsPage() {
  const { user } = useAuth();
  const enabled = !!user?.isAdmin;
  const [status, setStatus] = React.useState<ReferralStatus | "all">("review");
  const [flag, setFlag] = React.useState<ReferralFlag | "all">("all");
  // Default to review; when review is empty on first load, fall through to pending.
  const autoFellThrough = React.useRef(false);

  const summaryQuery = useQuery({ queryKey: SUMMARY_KEY, queryFn: fetchAdminReferrals, enabled });
  const eventsQuery = useQuery({
    queryKey: [...EVENTS_KEY, status, flag],
    queryFn: () =>
      fetchAdminReferralEvents({
        ...(status === "all" ? {} : { status }),
        ...(flag === "all" ? {} : { flag }),
      }),
    enabled,
  });

  React.useEffect(() => {
    if (autoFellThrough.current) return;
    if (status === "review" && flag === "all" && eventsQuery.data && eventsQuery.data.events.length === 0) {
      autoFellThrough.current = true;
      setStatus("pending");
    }
  }, [status, flag, eventsQuery.data]);

  if (!user?.isAdmin) return <AccessDenied />;

  const totals = summaryQuery.data?.totals;
  const events = eventsQuery.data?.events ?? [];

  return (
    <AdminShell title="Invites">
      {/* ── Totals ────────────────────────────────────────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12 }}>
        {summaryQuery.isLoading ? (
          <>
            <Skel height={96} /><Skel height={96} /><Skel height={96} /><Skel height={96} /><Skel height={96} /><Skel height={96} />
          </>
        ) : totals ? (
          <>
            <AtlasPanel bodyStyle={{ padding: "16px 18px" }}>
              <StatBlock size="sm" label="Attributed" value={totals.attributed.toLocaleString()} sub="sign-ups from a link" />
            </AtlasPanel>
            <AtlasPanel bodyStyle={{ padding: "16px 18px" }}>
              <StatBlock size="sm" label="Credited" value={totals.credited.toLocaleString()} gold sub="first walk logged" />
            </AtlasPanel>
            <AtlasPanel bodyStyle={{ padding: "16px 18px" }}>
              <StatBlock size="sm" label="Waiting" value={totals.pending.toLocaleString()} sub="pending a first walk" />
            </AtlasPanel>
            <AtlasPanel bodyStyle={{ padding: "16px 18px" }}>
              <StatBlock size="sm" label="In review" value={totals.review.toLocaleString()} sub={`${totals.rejected} rejected · ${totals.expired} expired`} />
            </AtlasPanel>
            <AtlasPanel bodyStyle={{ padding: "16px 18px" }}>
              <StatBlock size="sm" label="Waypoints paid" value={totals.waypointsPaid.toLocaleString()} sub="both sides, all time" />
            </AtlasPanel>
            <AtlasPanel bodyStyle={{ padding: "16px 18px" }}>
              <StatBlock size="sm" label="Conversion" value={`${Math.round(totals.conversionPct)}%`} sub="attributed to credited" />
            </AtlasPanel>
          </>
        ) : null}
      </div>

      {summaryQuery.isError && (
        <p style={{ fontSize: 13, color: "var(--status-danger)", margin: 0 }}>
          {errorMessage(summaryQuery.error, "Failed to load totals")}
        </p>
      )}

      {/* ── Series + top referrers ────────────────────────────────────── */}
      <div className="r-cols-2" style={{ display: "grid", gridTemplateColumns: "minmax(0, 2fr) minmax(0, 1fr)", gap: 16 }}>
        <AtlasPanel title="Sign-ups and credits">
          {summaryQuery.isLoading ? <Skel height={160} /> : <ReferralLines points={summaryQuery.data?.series ?? []} />}
        </AtlasPanel>
        <AtlasPanel title="Top referrers">
          {summaryQuery.isLoading ? (
            <Skel height={120} />
          ) : (summaryQuery.data?.topReferrers.length ?? 0) === 0 ? (
            <p style={{ fontSize: 13, color: "var(--text-muted)", margin: 0 }}>No one has brought anyone in yet.</p>
          ) : (
            <div className="r-scroll-x" style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr>
                    <th className="eyebrow" style={thStyle}>Explorer</th>
                    <th className="eyebrow" style={{ ...thStyle, textAlign: "right" }}>Credited</th>
                    <th className="eyebrow" style={{ ...thStyle, textAlign: "right", paddingRight: 0 }}>Pending</th>
                  </tr>
                </thead>
                <tbody>
                  {summaryQuery.data!.topReferrers.map((r) => (
                    <tr key={r.userId}>
                      <td style={{ ...tdStyle, color: "var(--text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 180 }}>
                        {r.name?.trim() || r.username || r.userId.slice(0, 8)}
                        {r.username && <span style={{ fontFamily: "var(--font-mono)", color: "var(--text-muted)", marginLeft: 6, fontSize: 11 }}>@{r.username}</span>}
                      </td>
                      <td className="tnum" style={{ ...tdStyle, textAlign: "right", fontFamily: "var(--font-mono)" }}>{r.credited}</td>
                      <td className="tnum" style={{ ...tdStyle, textAlign: "right", fontFamily: "var(--font-mono)", paddingRight: 0, color: "var(--text-muted)" }}>{r.pending}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </AtlasPanel>
      </div>

      {/* ── Fraud list ────────────────────────────────────────────────── */}
      <AtlasPanel
        title="Referral events"
        action={
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
            <select value={status} onChange={(e) => setStatus(e.target.value as ReferralStatus | "all")} style={selectStyle} aria-label="Status">
              {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <select value={flag} onChange={(e) => setFlag(e.target.value as ReferralFlag | "all")} style={selectStyle} aria-label="Flag">
              {FLAG_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        }
      >
        {eventsQuery.isLoading ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <Skel height={40} /><Skel height={40} /><Skel height={40} />
          </div>
        ) : eventsQuery.isError ? (
          <p style={{ fontSize: 13, color: "var(--status-danger)", margin: 0 }}>
            {errorMessage(eventsQuery.error, "Failed to load events")}
          </p>
        ) : events.length === 0 ? (
          <EmptyState
            icon={status === "review" ? <ShieldAlert size={20} /> : <UserPlus size={20} />}
            title={status === "review" ? "Nothing in review" : "No referral events match"}
            hint={status === "review" ? "Flagged sign-ups land here for a human decision. Quiet is good." : "Try another status or clear the flag filter."}
          />
        ) : (
          <div className="r-scroll-x" style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1040 }}>
              <thead>
                <tr>
                  <th className="eyebrow" style={thStyle}>Created</th>
                  <th className="eyebrow" style={thStyle}>Referrer</th>
                  <th className="eyebrow" style={thStyle}>Invitee</th>
                  <th className="eyebrow" style={thStyle}>Source</th>
                  <th className="eyebrow" style={thStyle}>Flags</th>
                  <th className="eyebrow" style={{ ...thStyle, textAlign: "right" }}>IP collisions</th>
                  <th className="eyebrow" style={thStyle}>Status</th>
                  <th className="eyebrow" style={thStyle}>Note</th>
                  <th className="eyebrow" style={{ ...thStyle, textAlign: "right", paddingRight: 0 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {events.map((event) => <EventRow key={event.id} event={event} />)}
              </tbody>
            </table>
          </div>
        )}
      </AtlasPanel>
    </AdminShell>
  );
}
